import { useEffect, useMemo, useState } from "react";
import { MONEY_METRICS, PAYMENT_STATUS_OPTIONS } from "../adminConstants";
import { DATE_FORMATTER, MONTH_FORMATTER, formatCurrency } from "../adminUtils";
import { getAdminPayments } from "../../../api/adminPayments";
import { getAdminMoneyReport } from "../../../api/adminReports";
import {
  exportAdminAccountant,
  exportAdminRevenue,
  exportAdminVat,
} from "../../../api/adminExports";

const useAdminPayments = ({ t, dashboardCurrency, showFeedback }) => {
  const [moneyMonth, setMoneyMonth] = useState(() => new Date().getMonth());
  const [moneyYear, setMoneyYear] = useState(() => new Date().getFullYear());
  const [moneyMetric, setMoneyMetric] = useState("revenue");
  const [moneyReport, setMoneyReport] = useState(null);
  const [moneyLoading, setMoneyLoading] = useState(false);
  const [moneyError, setMoneyError] = useState("");
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsError, setPaymentsError] = useState("");
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [paymentsPageSize] = useState(10);
  const [expandedPaymentRowId, setExpandedPaymentRowId] = useState(null);
  const [paymentSearchQuery, setPaymentSearchQuery] = useState("");
  const [paymentAppliedQuery, setPaymentAppliedQuery] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [paymentTypeFilter, setPaymentTypeFilter] = useState("all");
  const [paymentSortTimeDir, setPaymentSortTimeDir] = useState("desc");
  const [paymentSortAmountDir, setPaymentSortAmountDir] = useState("none");

  const isYearView = moneyMonth === -1;

  const moneyMonthOptions = useMemo(() => {
    const months = Array.from({ length: 12 }, (_, index) => ({
      value: index,
      label: MONTH_FORMATTER.format(new Date(2024, index, 1)),
    }));
    return [
      { value: -1, label: t("admin.money.yearView", "Full year") },
      ...months,
    ];
  }, [t]);

  const moneyYearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, index) => currentYear - 3 + index);
  }, []);

  const moneyDaily = useMemo(() => {
    if (!Array.isArray(moneyReport?.days)) return [];
    const sorted = [...moneyReport.days].sort((a, b) => {
      const aTime = new Date(a.dateUtc).getTime();
      const bTime = new Date(b.dateUtc).getTime();
      return aTime - bTime;
    });
    return sorted.map((day) => {
      const date = new Date(day.dateUtc);
      const dayNumber = Number.isNaN(date.getTime()) ? 0 : date.getDate();
      return {
        day: dayNumber,
        label: Number.isNaN(date.getTime()) ? "-" : DATE_FORMATTER.format(date),
        date,
        key: day.dateUtc,
        revenue: day.revenue ?? 0,
        refunds: day.refunds ?? 0,
        profit: day.profit ?? 0,
      };
    });
  }, [moneyReport]);

  const moneyMonthly = useMemo(() => {
    if (!isYearView) return [];
    const months = Array.from({ length: 12 }, (_, index) => ({
      month: index + 1,
      label: MONTH_FORMATTER.format(new Date(moneyYear, index, 1)),
      revenue: 0,
      refunds: 0,
      profit: 0,
    }));
    moneyDaily.forEach((day) => {
      if (Number.isNaN(day.date?.getTime())) return;
      const monthIndex = day.date.getMonth();
      const target = months[monthIndex];
      if (!target) return;
      target.revenue += day.revenue;
      target.refunds += day.refunds;
      target.profit += day.profit;
    });
    return months;
  }, [isYearView, moneyDaily, moneyYear]);

  const moneyChartData = useMemo(
    () => (isYearView ? moneyMonthly : moneyDaily),
    [isYearView, moneyMonthly, moneyDaily]
  );

  const moneyMetricKey = useMemo(
    () => (moneyMetric === "expenses" ? "refunds" : moneyMetric),
    [moneyMetric]
  );

  const moneyChartStyle = useMemo(() => {
    if (moneyMetric === "profit") {
      return { gradientId: "moneyProfit", stroke: "#176D7A" };
    }
    if (moneyMetric === "expenses") {
      return { gradientId: "moneyRefunds", stroke: "#CE6F3F" };
    }
    return { gradientId: "moneyRevenue", stroke: "#208E76" };
  }, [moneyMetric]);

  const moneyMetricLabel = useMemo(() => {
    const metric = MONEY_METRICS.find((item) => item.id === moneyMetric);
    return metric ? t(metric.labelKey, metric.labelFallback) : moneyMetric;
  }, [moneyMetric, t]);

  const moneyMonthLabel = useMemo(() => {
    if (isYearView) return "";
    return MONTH_FORMATTER.format(new Date(moneyYear, moneyMonth, 1));
  }, [isYearView, moneyMonth, moneyYear]);

  const moneyRangeLabel = useMemo(
    () => (isYearView ? String(moneyYear) : `${moneyMonthLabel} ${moneyYear}`),
    [isYearView, moneyMonthLabel, moneyYear]
  );

  const moneyCurrency = moneyReport?.currency || dashboardCurrency;

  const moneyTotals = useMemo(() => {
    return moneyDaily.reduce(
      (acc, day) => {
        acc.revenue += day.revenue;
        acc.refunds += day.refunds;
        acc.profit += day.profit;
        return acc;
      },
      { revenue: 0, refunds: 0, profit: 0 }
    );
  }, [moneyDaily]);

  const moneyHasData = useMemo(
    () => moneyDaily.some((entry) => entry.revenue > 0 || entry.refunds > 0 || entry.profit > 0),
    [moneyDaily]
  );

  const moneyMetricTotal = useMemo(
    () =>
      moneyChartData.reduce(
        (sum, entry) => sum + (Number.isFinite(entry[moneyMetricKey]) ? entry[moneyMetricKey] : 0),
        0
      ),
    [moneyChartData, moneyMetricKey]
  );

  const moneySummaryCards = useMemo(
    () => [
      {
        id: "money-revenue",
        label: t("admin.money.totalRevenue", "Total revenue"),
        value: formatCurrency(moneyTotals.revenue, moneyCurrency),
        detail: moneyRangeLabel,
        tone: "mint",
      },
      {
        id: "money-refunds",
        label: t("admin.money.totalRefunds", "Refunds issued"),
        value: formatCurrency(moneyTotals.refunds, moneyCurrency),
        detail: moneyRangeLabel,
        tone: "sun",
      },
      {
        id: "money-profit",
        label: t("admin.money.totalProfit", "Net profit"),
        value: formatCurrency(moneyTotals.profit, moneyCurrency),
        detail: moneyRangeLabel,
        tone: "ink",
      },
    ],
    [moneyCurrency, moneyRangeLabel, moneyTotals, t]
  );

  const paymentStatusOptions = useMemo(() => PAYMENT_STATUS_OPTIONS, []);

  useEffect(() => {
    let cancelled = false;

    const loadMoneyReport = async () => {
      setMoneyLoading(true);
      setMoneyError("");
      try {
        const start = isYearView
          ? new Date(Date.UTC(moneyYear, 0, 1))
          : new Date(Date.UTC(moneyYear, moneyMonth, 1));
        const end = isYearView
          ? new Date(Date.UTC(moneyYear + 1, 0, 1))
          : new Date(Date.UTC(moneyYear, moneyMonth + 1, 1));
        const payload = await getAdminMoneyReport({ from: start, to: end });
        if (!cancelled) {
          setMoneyReport(payload);
        }
      } catch (error) {
        if (!cancelled) {
          setMoneyError(error?.message || "Failed to load money overview.");
          setMoneyReport(null);
        }
      } finally {
        if (!cancelled) setMoneyLoading(false);
      }
    };

    loadMoneyReport();

    return () => {
      cancelled = true;
    };
  }, [dashboardCurrency, isYearView, moneyMonth, moneyYear]);

  useEffect(() => {
    let cancelled = false;

    const loadPayments = async () => {
      setPaymentsLoading(true);
      setPaymentsError("");
      try {
        const start = isYearView
          ? new Date(Date.UTC(moneyYear, 0, 1))
          : new Date(Date.UTC(moneyYear, moneyMonth, 1));
        const end = isYearView
          ? new Date(Date.UTC(moneyYear + 1, 0, 1))
          : new Date(Date.UTC(moneyYear, moneyMonth + 1, 1));
        const payload = await getAdminPayments({
          from: start,
          to: end,
          page: 1,
          pageSize: 200,
        });
        if (cancelled) return;
        const items = Array.isArray(payload?.items) ? payload.items : [];
        setPayments(items);
      } catch (error) {
        if (cancelled) return;
        setPaymentsError(error?.message || "Failed to load payments.");
        setPayments([]);
      } finally {
        if (!cancelled) setPaymentsLoading(false);
      }
    };

    loadPayments();

    return () => {
      cancelled = true;
    };
  }, [isYearView, moneyMonth, moneyYear]);

  const handlePaymentSearch = () => {
    const query = paymentSearchQuery.trim();
    setPaymentAppliedQuery(query);
    setPaymentsPage(1);
  };

  const handleResetPaymentFilters = () => {
    setPaymentSearchQuery("");
    setPaymentAppliedQuery("");
    setPaymentStatusFilter("all");
    setPaymentTypeFilter("all");
    setPaymentSortTimeDir("desc");
    setPaymentSortAmountDir("none");
    setPaymentsPage(1);
    showFeedback("Payment filters reset.", "info");
  };

  const handlePaymentRowToggle = (rowId) => {
    setExpandedPaymentRowId((prev) => (prev === rowId ? null : rowId));
  };

  const downloadBlob = (blob, fileName, fallback) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download =
      fileName ||
      fallback ||
      `wayli-export-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleExportMonthlyRevenue = async () => {
    try {
      const params = {
        year: moneyYear,
        month: isYearView ? undefined : moneyMonth + 1,
      };
      const { blob, fileName } = await exportAdminRevenue(params);
      downloadBlob(blob, fileName, "wayli-revenue.csv");
      showFeedback("Revenue export downloaded.", "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to export revenue.", "warning");
    }
  };

  const handleExportVatReady = async () => {
    try {
      const params = {
        year: moneyYear,
        month: isYearView ? undefined : moneyMonth + 1,
      };
      const { blob, fileName } = await exportAdminVat(params);
      downloadBlob(blob, fileName, "wayli-vat.csv");
      showFeedback("VAT export downloaded.", "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to export VAT report.", "warning");
    }
  };

  const handleExportAccountantCsv = async () => {
    try {
      const params = {
        year: moneyYear,
        month: isYearView ? undefined : moneyMonth + 1,
      };
      const { blob, fileName } = await exportAdminAccountant(params);
      downloadBlob(blob, fileName, "wayli-accountant.csv");
      showFeedback("Accountant export downloaded.", "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to export accountant pack.", "warning");
    }
  };

  return {
    moneyMonth,
    setMoneyMonth,
    moneyYear,
    setMoneyYear,
    moneyMetric,
    setMoneyMetric,
    moneyReport,
    moneyLoading,
    moneyError,
    moneyMonthOptions,
    moneyYearOptions,
    moneyChartData,
    moneyMetricKey,
    moneyChartStyle,
    moneyMetricLabel,
    moneyRangeLabel,
    moneyMetricTotal,
    moneyCurrency,
    moneyHasData,
    moneySummaryCards,
    payments,
    paymentsLoading,
    paymentsError,
    paymentsPage,
    setPaymentsPage,
    paymentsPageSize,
    expandedPaymentRowId,
    paymentSearchQuery,
    setPaymentSearchQuery,
    paymentAppliedQuery,
    paymentStatusFilter,
    setPaymentStatusFilter,
    paymentTypeFilter,
    setPaymentTypeFilter,
    paymentStatusOptions,
    paymentSortTimeDir,
    setPaymentSortTimeDir,
    paymentSortAmountDir,
    setPaymentSortAmountDir,
    handlePaymentSearch,
    handleResetPaymentFilters,
    handlePaymentRowToggle,
    handleExportMonthlyRevenue,
    handleExportVatReady,
    handleExportAccountantCsv,
  };
};

export default useAdminPayments;
